"use client";

import { Plus, Trash2 } from "lucide-react";
import type { ResumeDocument } from "@/types/cv";
import { createEmptyEducation } from "@/lib/cv-builder/resume-factory";
import { CV_BUILDER_EXAMPLE } from "@/components/cv-builder/sample-resume";
import { TextField, MonthField } from "@/components/cv-builder/fields";
import { Button } from "@/components/ui/button";

interface StepProps {
  resume: ResumeDocument;
  onChange: (next: ResumeDocument) => void;
}

export function EducationStep({ resume, onChange }: StepProps) {
  const entries = resume.education;

  const patch = (index: number, value: ResumeDocument["education"][number]) =>
    onChange({
      ...resume,
      education: entries.map((entry, i) => (i === index ? value : entry)),
    });

  const remove = (index: number) =>
    onChange({ ...resume, education: entries.filter((_, i) => i !== index) });

  const add = () =>
    onChange({ ...resume, education: [...entries, createEmptyEducation()] });

  return (
    <div className="space-y-5">
      {entries.map((entry, index) => (
        <div
          key={index}
          className="space-y-4 rounded-xl border border-border bg-card p-4"
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-muted-foreground">
              Pendidikan {index + 1}
            </p>
            {entries.length > 1 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => remove(index)}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Hapus
              </Button>
            ) : null}
          </div>
          <TextField
            label="Sekolah / kampus"
            value={entry.institution}
            onChange={(institution) => patch(index, { ...entry, institution })}
            placeholder={CV_BUILDER_EXAMPLE.institution}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Jenjang"
              value={entry.degree}
              onChange={(degree) => patch(index, { ...entry, degree })}
              placeholder={CV_BUILDER_EXAMPLE.degree}
            />
            <TextField
              label="Jurusan"
              optional
              value={entry.major}
              onChange={(major) => patch(index, { ...entry, major })}
              placeholder={CV_BUILDER_EXAMPLE.major}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MonthField
              label="Mulai"
              value={entry.start_date}
              onChange={(start_date) => patch(index, { ...entry, start_date })}
            />
            <MonthField
              label="Lulus"
              value={entry.end_date}
              onChange={(end_date) => patch(index, { ...entry, end_date })}
            />
          </div>
          <TextField
            label="IPK / nilai"
            optional
            value={entry.gpa}
            onChange={(gpa) => patch(index, { ...entry, gpa })}
            placeholder={CV_BUILDER_EXAMPLE.gpa}
          />
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} className="w-full">
        <Plus className="mr-1.5 h-4 w-4" />
        Tambah pendidikan
      </Button>
    </div>
  );
}
